type PlatformKey = 'twitch' | 'youtube' | 'kick';

export type PlatformMeta = {
  key: PlatformKey;
  label: string;
  color: string;
  urlBase: string;
  routePrefix: 't' | 'y' | 'k';
};

export const PLATFORM_META: Record<PlatformKey, PlatformMeta> = {
  twitch: {
    key: 'twitch',
    label: 'Twitch',
    color: '#9146FF',
    urlBase: 'https://twitch.tv/',
    routePrefix: 't',
  },
  youtube: {
    key: 'youtube',
    label: 'YouTube',
    color: '#FF0033',
    urlBase: 'https://youtube.com/@',
    routePrefix: 'y',
  },
  kick: {
    key: 'kick',
    label: 'Kick',
    color: '#53FC18',
    urlBase: 'https://kick.com/',
    routePrefix: 'k',
  },
};

/**
 * A YouTube handle is stored with its leading `@` -- the streamer page is
 * `/y/@brukos` -- and youtube.com's profile URL carries one of its own, so
 * joining the two naively renders `youtube.com/@@brukos`.
 */
export function platformProfileUrl(platform: PlatformKey, username: string): string {
  const { urlBase } = PLATFORM_META[platform];

  return `${urlBase}${urlBase.endsWith('@') ? username.replace(/^@+/, '') : username}`;
}

/** The same link without its scheme, so it reads as a handle. */
export function platformProfileLabel(platform: PlatformKey, username: string): string {
  return platformProfileUrl(platform, username).replace(/^https:\/\//, '');
}

export type PlatformGlyphProps = {
  platform: PlatformKey;
  size?: number;
};

export function PlatformGlyph({ platform, size = 14 }: PlatformGlyphProps) {
  const color = PLATFORM_META[platform].color;
  if (platform === 'twitch') {
    return (
      <svg width={size} height={size} viewBox='0 0 24 24' fill={color} aria-hidden='true'>
        <path d='M4.265 3 3 6.561v12.78h4.434V21.5h2.532l2.155-2.16h3.564L21 14.118V3H4.265zm15.219 10.276-2.658 2.661h-4.434L9.964 17.94v-2.003H6.137V4.534h13.347v8.742zM17.34 7.184h-1.518v4.514h1.518V7.184zm-4.182 0h-1.518v4.514h1.518V7.184z' />
      </svg>
    );
  }
  if (platform === 'youtube') {
    return (
      <svg width={size} height={size} viewBox='0 0 24 24' fill={color} aria-hidden='true'>
        <path d='M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z' />
      </svg>
    );
  }
  return (
    <svg width={size} height={size} viewBox='0 0 24 24' fill={color} aria-hidden='true'>
      <path d='M3 3h5v4h2V5h2V3h5v6h-2v2h-2v2h2v2h2v6h-5v-2h-2v-2H8v4H3V3z' />
    </svg>
  );
}

export function isPlatformKey(value: string): value is PlatformKey {
  return value === 'twitch' || value === 'youtube' || value === 'kick';
}
